import { queryOptions, useQuery } from '@tanstack/react-query';

import { WorkloadGpuUtilizationSummary } from '@cast/types';

import { apiClient } from '@cast/infrastructure-api';

const WORKLOAD_GPU_UTILIZATION_SUMMARY_QUERY_KEY =
  'workload-gpu-utilization-summary';

const getWorkloadGpuUtilizationSummaryQueryKey = ({
  clusterId,
  workloadName,
  workloadType,
  namespace,
  startTime,
  endTime,
  useListingPrices,
}: Partial<UseWorkloadGpuUtilizationSummaryQueryProps> = {}) =>
  [
    WORKLOAD_GPU_UTILIZATION_SUMMARY_QUERY_KEY,
    ...(clusterId !== undefined
      ? [
          clusterId,
          workloadName,
          workloadType,
          namespace,
          startTime,
          endTime,
          useListingPrices,
        ]
      : []),
  ] as const;

type UseWorkloadGpuUtilizationSummaryQueryProps = {
  clusterId: string;
  workloadName: string;
  workloadType: string;
  namespace: string;
  startTime: string;
  endTime: string;
  useListingPrices: boolean;
  enabled?: boolean;
};

type UseWorkloadGpuUtilizationSummaryQuery = {
  data?: WorkloadGpuUtilizationSummary;
  isLoading: boolean;
  error?: unknown;
  refetch: () => void;
};

const getWorkloadGpuUtilizationSummaryQueryOptions = ({
  clusterId,
  workloadName,
  workloadType,
  namespace,
  startTime,
  endTime,
  useListingPrices,
  enabled = true,
}: UseWorkloadGpuUtilizationSummaryQueryProps) =>
  queryOptions({
    queryKey: getWorkloadGpuUtilizationSummaryQueryKey({
      clusterId,
      workloadName,
      workloadType,
      namespace,
      startTime,
      endTime,
      useListingPrices,
    }),
    enabled:
      enabled &&
      Boolean(clusterId) &&
      Boolean(workloadName) &&
      Boolean(workloadType) &&
      Boolean(namespace) &&
      Boolean(startTime) &&
      Boolean(endTime),
    queryFn: async () => {
      const { data } =
        await apiClient.costReport.getWorkloadGpuUtilizationSummary({
          clusterId,
          workloadName,
          workloadType,
          namespace,
          startTime,
          endTime,
          useListingPrices,
        });

      return data;
    },
  });

export const useWorkloadGpuUtilizationSummaryQuery = (
  props: UseWorkloadGpuUtilizationSummaryQueryProps
): UseWorkloadGpuUtilizationSummaryQuery => {
  const { data, isLoading, error, refetch } = useQuery(
    getWorkloadGpuUtilizationSummaryQueryOptions(props)
  );

  return {
    isLoading,
    data,
    error,
    refetch,
  };
};
